import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import Paper from '@mui/material/Paper'
import LinkIcon from '@mui/icons-material/Link'

import Button from '@/v2/components/common/Button'
import Typography from '@/v2/components/common/Typography'

import { usePatientContext } from '@/v2/contexts/PatientContext'
import {
  useFamilyMembers,
  useFamilyMemberLinks,
  RELATIONSHIP_OPTIONS,
} from '@/v2/hooks/patient/useFamilyMembers'
import FamilyMembersModal from './FamilyMembersModal'

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 24px;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 20px;
  column-gap: 24px;
`

const MemberCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const PrimaryPill = styled.span`
  align-self: flex-start;
  background-color: #e8e8e8;
  color: #4a4a4a;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 3px 10px;
  border-radius: 4px;
  text-transform: uppercase;
`

const EmptyState = styled.div`
  background: #f4f4f4;
  text-align: center;
  padding: 24px 0;
  border-radius: 4px;
  opacity: 0.7;
`

const NameRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const LinkedName = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`

const LinksSection = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #eeeded;
`

const LinkItem = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  background: none;
  border: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
  width: 100%;

  &:hover .link-patient-name {
    text-decoration: underline;
  }
`

const RELATIONSHIP_LABELS = RELATIONSHIP_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label
  return acc
}, {})

const formatRelationship = value =>
  RELATIONSHIP_LABELS[value] || value || 'Family member'

const FamilyMembers = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { patient } = usePatientContext()
  const patientId = patient?.id

  const { data: members, isLoading } = useFamilyMembers(patientId)
  const { data: inverseLinks } = useFamilyMemberLinks(patientId)

  const [showModal, setShowModal] = useState(false)

  const goToPatient = id => navigate(`/v2/patient/${id}/profile`)

  return (
    <Paper sx={{ padding: '30px', marginBottom: '20px' }}>
      <Header>
        <Typography variant="h3">Family Members</Typography>
        <Button
          variant="text"
          onClick={() => setShowModal(true)}
          sx={{ color: theme.palette.gray.dark }}
        >
          <Typography variant="h5">Edit</Typography>
        </Button>
      </Header>

      {isLoading ? (
        <EmptyState>
          <Typography>Loading...</Typography>
        </EmptyState>
      ) : members.length === 0 ? (
        <EmptyState>
          <Typography>
            No family members yet. Click "Edit" to add one.
          </Typography>
        </EmptyState>
      ) : (
        <Grid>
          {members.map(member => (
            <MemberCell key={member.id}>
              <NameRow>
                {member.relatedPatientId ? (
                  <LinkedName
                    type="button"
                    onClick={() => goToPatient(member.relatedPatientId)}
                  >
                    <Typography bold fontSize="15px">
                      {member.fullName}
                    </Typography>
                  </LinkedName>
                ) : (
                  <Typography bold fontSize="15px">
                    {member.fullName}
                  </Typography>
                )}
                {member.relatedPatientId && (
                  <LinkIcon
                    fontSize="small"
                    sx={{ color: theme.palette.gray.dark }}
                  />
                )}
              </NameRow>
              <Typography
                fontSize="14px"
                sx={{ color: theme.palette.gray.dark }}
              >
                {formatRelationship(member.relationship)}
              </Typography>
              {member.main && <PrimaryPill>PRIMARY</PrimaryPill>}
            </MemberCell>
          ))}
        </Grid>
      )}

      {inverseLinks.length > 0 && (
        <LinksSection>
          <Typography
            variant="h5"
            sx={{ color: theme.palette.gray.dark, marginBottom: '8px' }}
          >
            Listed as family member of
          </Typography>
          {inverseLinks.map(link => (
            <LinkItem
              key={link.id}
              type="button"
              onClick={() => goToPatient(link.patientId)}
            >
              <LinkIcon
                fontSize="small"
                sx={{ color: theme.palette.gray.dark }}
              />
              <Typography fontSize="14px">
                {formatRelationship(link.relationship)} of{' '}
                <span className="link-patient-name">{link.patientName}</span>
              </Typography>
            </LinkItem>
          ))}
        </LinksSection>
      )}

      {showModal && (
        <FamilyMembersModal
          isOpen
          onClose={() => setShowModal(false)}
          patientId={patientId}
        />
      )}
    </Paper>
  )
}

export default FamilyMembers
